<template>
  <main>
    <div
      v-for="(boardRow, ii) in board"
      :key="ii"
      :style="{fontSize: 0}"
    >
      <square
        v-for="(squareType, jj) in boardRow"
        :key="jj"
        :squareType="squareType"
        :ii="ii"
        :jj="jj"
        :disabled="isInSwap(ii, jj, lastSwap)"
        :active="active[0] === ii && active[1] === jj"
        @clicked="onClick(ii, jj)"
      />
    </div>
    <div class="mt-3">
      <h3 v-if="gameOver">
        {{ this.whoWon === null ? 'Its a draw!' : getColor(whoWon) + ' won!' }}
      </h3>
      <h3 v-else>
        It's <font :color="getColorCode(turn)">{{ getColor(turn) }} </font>
      </h3>
    </div>
    <div>
      <b-dropdown
        class="mr-2 mt-3"
        :text="boardLengthText"
      >
        <b-dropdown-item
          v-for="(option, ii) in boardLengthOptions"
          :key="ii"
          @click="assignBoardLength(option)"
        >
          {{ option }}
        </b-dropdown-item>
      </b-dropdown>
      <b-dropdown
        class="mr-2 mt-3"
        :text="computerDropdownText"
        :variant="computerDropdownVariant"
      >
        <b-dropdown-item
          v-for="(option, ii) in computerOptions"
          :key="ii"
          @click="assignComputer(option.value)"
        >
          {{ option.text }}
        </b-dropdown-item>
      </b-dropdown>
      <b-btn
        class="mt-3"
        variant="warning"
        @click="resetGame"
      >
        Restart
      </b-btn>
    </div>
  </main>
</template>

<script>

import axios from 'axios';
import { endpoints } from '../../constants';
import square from './square.vue';
import initialization from './initialization';
import {
  swap, countSquares, conquer, isSameSwap,
} from './utils';


const computerMap = {
  0: { text: 'Blue', variant: 'primary' },
  1: { text: 'Red', variant: 'danger' },
  2: { text: 'Disabled', variant: 'secondary' },
};


export default {
  components: { square },
  name: 'board',
  data() {
    return {
      board: [],
      active: [],
      lastSwap: null,
      turn: 0,
      squareCount: null,
      computer: 2,
      computerOptions: [
        { text: 'Blue', value: 0 },
        { text: 'Red', value: 1 },
        { text: 'Disabled', value: 2 },
      ],
      boardLength: 4,
      boardLengthOptions: [3, 4, 5],
    };
  },
  created() {
    this.resetGame();
  },
  watch: {
    boardLength() {
      this.resetGame();
    },
    turn() {
      this.aiMoveWatcher();
    },
    computer() {
      this.aiMoveWatcher();
    },
  },
  computed: {
    computerDropdownText() {
      return `Computer is ${computerMap[this.computer].text}`;
    },
    computerDropdownVariant() {
      return computerMap[this.computer].variant;
    },
    boardLengthText() {
      return `Board length is ${this.boardLength}`;
    },
    gameOver() {
      if (this.squareCount && this.squareCount[2] === 0) {
        return true;
      }
      return false;
    },
    whoWon() {
      if (this.gameOver) {
        if (this.squareCount[0] > this.squareCount[1]) {
          return 0;
        }
        if (this.squareCount[1] > this.squareCount[0]) {
          return 1;
        }
      }
      return null;
    },
  },
  methods: {
    async aiMoveWatcher() {
      if (!this.gameOver && this.turn === this.computer) {
        await new Promise(r => setTimeout(r, 1000));
        this.aiMove();
      }
    },
    assignComputer(val) {
      this.computer = val;
    },
    assignBoardLength(val) {
      this.boardLength = val;
    },
    resetGame() {
      this.computer = 2;
      this.active = [];
      this.lastSwap = null;
      this.turn = 0;
      this.board = initialization(this.boardLength);
      this.squareCount = countSquares(this.board);
    },
    async aiMove() {
      const data = {
        board: this.board,
        turn: this.turn,
        n: this.boardLength,
        strategy: 'td0',
        lastSwap: this.lastSwap,
      };
      this.thinking = true;
      const resp = await axios.post(endpoints.makeMove, data);
      this.thinking = false;
      const { type } = resp.data;
      const { move } = resp.data;
      if (type === 'pick') {
        this.pickSquare(move[0] + 1, move[1] + 1);
      } else if (type === 'swapRow') {
        this.active = [move[0] + 1, 0];
        this.doSwap(move[1] + 1, 0);
      } else if (type === 'swapCol') {
        this.active = [0, move[0] + 1];
        this.doSwap(0, move[1] + 1);
      } else {
        console.log('unknown move type');
      }
    },
    onClick(ii, jj) {
      if (this.gameOver || (ii === 0 && jj === 0)) {
        return;
      }
      // axis swapping
      if (ii === 0 || jj === 0) {
        if (this.active.length) {
          // do not allow swapping back
          if (isSameSwap(this.lastSwap, [this.active, [ii, jj]])) {
            return;
          }
          // make inactive
          if (this.active[0] === ii && this.active[1] === jj) {
            this.active = [];
            return;
          }
          // do swap
          this.doSwap(ii, jj);
          return;
        }
        // make active
        this.active = [ii, jj];
        return;
      }
      // pick square
      this.pickSquare(ii, jj);
    },
    pickSquare(ii, jj) {
      if (this.board[ii][jj] === 2) {
        this.updateSquare(ii, jj, this.turn);
        this.lastSwap = null;
        this.endTurn();
      }
    },
    doSwap(ii, jj) {
      if (this.active[0] === ii || this.active[1] === jj) {
        this.board = swap(this.board, this.active, ii, jj);
        this.lastSwap = [this.active, [ii, jj]];
        this.endTurn();
      }
    },
    endTurn() {
      conquer(this.board, this.turn);
      this.squareCount = countSquares(this.board);
      this.turn = (this.turn + 1) % 2;
      this.active = [];
    },
    getColor(type) {
      return type ? 'red' : 'blue';
    },
    getColorCode(type) {
      return type ? '#ff884d' : '#80bfff';
    },
    updateSquare(ii, jj, val) {
      const row = this.board[ii];
      row[jj] = val;
      this.$set(this.board, ii, row);
    },
    isInSwap(ii, jj) {
      if (this.lastSwap === null) {
        return false;
      }
      if (this.lastSwap[0][0] === ii && this.lastSwap[0][1] === jj) {
        return true;
      }
      if (this.lastSwap[1][0] === ii && this.lastSwap[1][1] === jj) {
        return true;
      }
      return false;
    },
  },
};
</script>

<style scoped>

</style>
